import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { saveAs } from "file-saver";

const waitForPaint = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });

const restoreStyles = (entries) => {
  entries.forEach(({ node, styles }) => {
    Object.entries(styles).forEach(([key, value]) => {
      if (value == null || value === "") {
        node.style.removeProperty(key);
      } else {
        node.style.setProperty(key, value);
      }
    });
  });
};

const unlockScrollContainers = (rootEl) => {
  const entries = [];
  let node = rootEl;
  while (node) {
    const styles = {
      overflow: node.style.overflow,
      "max-height": node.style.maxHeight,
      height: node.style.height,
    };
    const computed = window.getComputedStyle(node);
    if (computed.overflowY === "auto" || computed.overflowY === "scroll" || computed.maxHeight !== "none") {
      node.style.overflow = "visible";
      node.style.maxHeight = "none";
      if (node.classList.contains("panel-modal-content--break-overview")) {
        node.style.height = "auto";
        entries.push({ node, styles: { ...styles, height: node.style.height } });
      } else {
        entries.push({ node, styles });
      }
    }
    if (node.classList.contains("panel-modal-content")) break;
    node = node.parentElement;
  }
  return entries;
};

export const buildBreakOverviewFileName = (agentName, periodStart, periodEnd, ext) => {
  const safe = String(agentName || "agent")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^\w\-]/g, "");
  const start = String(periodStart || "").replace(/-/g, "");
  const end = String(periodEnd || "").replace(/-/g, "");
  return `Ozel_Analiz_${safe}_${start}_${end}.${ext}`;
};

export async function captureBreakOverviewElement(element, { isDark = false } = {}) {
  if (!element) throw new Error("Export alanı bulunamadı");

  const styleEntries = unlockScrollContainers(element);
  await waitForPaint();
  await new Promise((r) => setTimeout(r, 200));

  try {
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: isDark ? "#0f172a" : "#ffffff",
      width: element.scrollWidth,
      height: element.scrollHeight,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
      scrollX: 0,
      scrollY: 0,
      logging: false,
      ignoreElements: (node) => {
        if (!(node instanceof HTMLElement)) return false;
        return node.closest("[data-export-hide]") != null;
      },
      onclone: (clonedDoc) => {
        clonedDoc.querySelectorAll(".break-analysis-day-table-label, .break-analysis-day-table th, .break-analysis-day-table td").forEach((node) => {
          node.style.position = "static";
          node.style.left = "auto";
          node.style.zIndex = "auto";
        });
        clonedDoc.querySelectorAll(".break-analysis-metric-value").forEach((node) => {
          node.style.display = "block";
          node.style.lineHeight = "1.35";
          node.style.marginBottom = "4px";
        });
      },
    });
    return canvas;
  } finally {
    restoreStyles(styleEntries);
  }
}

export async function exportBreakOverviewPng(element, options = {}) {
  const canvas = await captureBreakOverviewElement(element, options);
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("PNG oluşturulamadı"))), "image/png", 1);
  });
  saveAs(blob, options.fileName || "ozel_analiz.png");
}

export async function exportBreakOverviewPdf(element, options = {}) {
  const canvas = await captureBreakOverviewElement(element, options);
  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 8;
  const contentWidth = pageWidth - margin * 2;
  const imgHeight = (canvas.height * contentWidth) / canvas.width;

  let heightLeft = imgHeight;
  let position = margin;

  pdf.addImage(imgData, "PNG", margin, position, contentWidth, imgHeight, undefined, "FAST");
  heightLeft -= pageHeight - margin * 2;

  while (heightLeft > 0) {
    pdf.addPage();
    position = margin - (imgHeight - heightLeft);
    pdf.addImage(imgData, "PNG", margin, position, contentWidth, imgHeight, undefined, "FAST");
    heightLeft -= pageHeight - margin * 2;
  }

  pdf.save(options.fileName || "ozel_analiz.pdf");
}
